import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle } from 'lucide-react';
import { AvailableSubscriptionResource, AssignSubscriptionRequest } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface AssignSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSubscriptionAssigned: () => void;
}

const AssignSubscriptionDialog: React.FC<AssignSubscriptionDialogProps> = ({
  isOpen,
  onClose,
  companyId,
  onSubscriptionAssigned
}) => {
  const [subscriptions, setSubscriptions] = useState<AvailableSubscriptionResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Set minimum date to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  useEffect(() => {
    if (isOpen) {
      loadSubscriptions();
      setError(null);
    }
  }, [isOpen]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await api.getAvailableSubscriptions();
      setSubscriptions(data);
      if (data.length > 0) {
        setSelectedSubscriptionId(data[0].id);
        // Set default date to subscription months from now
        const defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() + data[0].months);
        setValidUntil(defaultDate.toISOString().split('T')[0]);
      }
    } catch (err) {
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSubscriptionId || !validUntil) {
      setError('Please select a subscription and date');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.assignSubscription(companyId, selectedSubscriptionId, { validUntil });
      onSubscriptionAssigned();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSubscription = subscriptions.find(s => s.id === selectedSubscriptionId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Assign Subscription</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <LoadingSpinner size="md" text="Loading subscriptions..." />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subscription Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Subscription
              </label>
              <select
                value={selectedSubscriptionId}
                onChange={(e) => {
                  setSelectedSubscriptionId(e.target.value);
                  if (e.target.value) {
                    const sub = subscriptions.find(s => s.id === e.target.value);
                    if (sub) {
                      const defaultDate = new Date();
                      defaultDate.setMonth(defaultDate.getMonth() + sub.months);
                      setValidUntil(defaultDate.toISOString().split('T')[0]);
                    }
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Choose a subscription...</option>
                {subscriptions.map((subscription) => (
                  <option key={subscription.id} value={subscription.id}>
                    {subscription.name} - {subscription.price.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })}/{subscription.months} month{subscription.months > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Subscription Details */}
            {selectedSubscription && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">{selectedSubscription.name}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Max Branches:</span>
                    <span className="ml-2 font-medium">{selectedSubscription.maxBranch}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Monthly Limit:</span>
                    <span className="ml-2 font-medium">
                      {selectedSubscription.maxTransactionPerMonth === 0 ? 'Unlimited' : selectedSubscription.maxTransactionPerMonth.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">F&B Mode:</span>
                    <span className={`ml-2 font-medium ${selectedSubscription.fnbMode ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedSubscription.fnbMode ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Duration:</span>
                    <span className="ml-2 font-medium">{selectedSubscription.months} month{selectedSubscription.months > 1 ? 's' : ''}</span>
                  </div>
                </div>
                {selectedSubscription.benefits && selectedSubscription.benefits.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Benefits:</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedSubscription.benefits.map((benefit, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Date Selection */}
            <div>
              <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700 mb-2">
                Valid Until Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  id="validUntil"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  min={minDate}
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={submitting}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select a date after today
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedSubscriptionId || !validUntil}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Assigning...' : 'Assign Subscription'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AssignSubscriptionDialog;
